import { useRef, useState, type UIEventHandler } from 'react';

import throttle from 'lodash/throttle';

interface CarouselScrollProps {
  /** The number of slides in the carousel. */
  numSlides: number;
  /** A function to update the active index in the state of the parent component. */
  handleUpdateCarousel: (index: number) => void;
  /** The width of each carousel item */
  itemWidth: number;
  /** The minimum number of visible items to display on scroll */
  minItems?: number;
}

/**
 * A hook that handles scrolling through a carousel.
 *
 * @param {CarouselScrollProps} props The props for the hook.
 * @returns {
 * * handleScroll: WheelEventHandler - attach to carousel container to handle the scroll event,
 * * scrollRef: RefObject<HTMLDivElement> - ref to the carousel container
 * * updateCarousel: (index: number) => void - attach to carousel container to handle touch end event,
 * }
 */

const useCarouselScroll = ({ numSlides, handleUpdateCarousel, itemWidth, minItems }: CarouselScrollProps) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollRef = useRef(null);

  const handleScroll: UIEventHandler<HTMLDivElement> = ({ target }) => {
    if (isScrolling) return;

    const { scrollLeft, offsetWidth } = target as HTMLDivElement;

    const minVisibleItems = minItems ?? Math.floor(offsetWidth / itemWidth);
    const maxScrollIndex = numSlides - minVisibleItems;
    const scrolledIndex = Math.min(Math.round(scrollLeft / itemWidth), maxScrollIndex);
    handleUpdateCarousel(scrolledIndex);
  };

  const updateCarousel = throttle((index: number) => {
    handleUpdateCarousel(index);

    if (!(scrollRef?.current as unknown as HTMLElement)?.scroll) return;

    setIsScrolling(true);

    (scrollRef.current as unknown as HTMLElement).scroll({
      left: index * itemWidth || 0,
      behavior: 'smooth',
    });

    (scrollRef.current as unknown as HTMLElement).addEventListener('scrollend', () => setIsScrolling(false));
  }, 30);

  return {
    handleScroll,
    scrollRef,
    updateCarousel,
  };
};

export default useCarouselScroll;
